import {
  Alert,
  Button,
  ButtonGroup,
  DataStatus,
  openConfirmDialog,
  showNotification,
  useDialog,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  VehicleFinancingType,
  useActivateAutomaticSaleVehicleFinancingMutation,
  useDeleteSaleVehicleFinancingMutation,
} from '@dms/api/vehicle';
import {DEFAULT_BALLOON_PAYMENT} from '@dms/features/vehicle-financing-template';
import i18n from '@dms/i18n';
import {COUNTRY_DEU, handleApiError, useTenant} from '@dms/shared';

import {PendingChangesBar} from '../../../../../../components/PendingChangesBar/PendingChangesBar';
import {FinancingBasicInformation} from './FinancingBasicInformation';
import {FinancingCellEditorDialog} from './FinancingCellEditorDialog';
import {FinancingConfigurationDialog} from './FinancingConfigurationDialog';
import {FinancingEmptyState} from './FinancingEmptyState';
import {FinancingOverrides} from './FinancingOverrides';
import {FinancingRateMatrixSection} from './FinancingRateMatrixSection';
import {FinancingTypeSegment} from './FinancingTypeSegment';
import {useFinancingOverview} from './hooks/useFinancingOverview';
import {useFinancingTemplateSelection} from './hooks/useFinancingTemplateSelection';
import {useVehicleFinancingDrafts} from './hooks/useVehicleFinancingDrafts';
import {FinancingCellCoordinates} from './types/FinancingCellCoordinates';
import {FinancingDisplayMode} from './types/FinancingDisplayMode';
import {PendingEditorChangeHandler} from './types/PendingEditor';
import {VehicleFinancingPricing} from './types/VehicleFinancingPricing';
import {createVehicleFinancingTemplateSeed} from './utils/createVehicleFinancingTemplateSeed';
import {createManualFinancingBody} from './utils/financingBody/createManualFinancingBody';
import {getBalloonBrackets} from './utils/financingCells/getBalloonBrackets';
import {hasValidActiveFinancingCell} from './utils/financingCells/hasValidActiveFinancingCell';
import {resolveActiveBalloonPayment} from './utils/financingCells/resolveActiveBalloonPayment';
import {getFinancingPendingChangesTitle} from './utils/getFinancingPendingChangesTitle';

const FINANCING_STATES_IN_EFFECT = ['EXPLICIT', 'AUTOMATIC'];

type FinancingConfigurationDialogMode = 'MANAGE' | 'CREATE';

interface FinancingPanelProps {
  saleVehicleId: string;
  currency: string;
  canReadTemplates: boolean;
  isCreateAllowed: boolean;
  isUpdateAllowed: boolean;
  isDeleteAllowed: boolean;
  onPendingChange?: PendingEditorChangeHandler;
  onSavingChange?: (isSaving: boolean) => void;
  onValidationError?: () => void;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function FinancingPanel(props: FinancingPanelProps) {
  const [activeType, setActiveType] = useState<VehicleFinancingType>('STANDARD');
  const [selectedBalloon, setSelectedBalloon] = useState<number | null>(null);
  const [displayMode, setDisplayMode] = useState<FinancingDisplayMode>('MATRIX');
  const [editedCellCoordinates, setEditedCellCoordinates] =
    useState<FinancingCellCoordinates | null>(null);
  const [matrixSelectionEpoch, setMatrixSelectionEpoch] = useState(0);
  const [
    isConfigurationDialogOpen,
    openConfigurationDialog,
    closeConfigurationDialog,
    {key: configurationDialogKey, storage: configurationDialogMode},
  ] = useDialog<FinancingConfigurationDialogMode>();
  const {isTenantCountrySupported} = useTenant();
  const isGermanTenant = isTenantCountrySupported(COUNTRY_DEU);

  const overview = useFinancingOverview({
    saleVehicleId: props.saleVehicleId,
    activeType,
    currency: props.currency,
    isCreateAllowed: props.isCreateAllowed,
    isUpdateAllowed: props.isUpdateAllowed,
    isGermanTenant,
  });
  const drafts = useVehicleFinancingDrafts({
    saleVehicleId: props.saleVehicleId,
    activeType,
    getPersistedBody: overview.getPersistedBody,
    isCreateAllowed: props.isCreateAllowed,
    isUpdateAllowed: props.isUpdateAllowed,
    onPendingChange: props.onPendingChange,
    onSavingChange: props.onSavingChange,
    onValidationError: (type) => {
      setActiveType(type);
      props.onValidationError?.();
    },
  });
  const [deleteFinancing, deleteStatus] = useDeleteSaleVehicleFinancingMutation();
  const [activateAutomaticFinancing, automaticStatus] =
    useActivateAutomaticSaleVehicleFinancingMutation();

  const vehicleFinancingBody = drafts.activeDraft ?? overview.persistedBody;

  const clearMatrixSelection = () => setMatrixSelectionEpoch((epoch) => epoch + 1);

  const clearActiveDraft = () => {
    drafts.discardActiveDraft();
    clearMatrixSelection();
  };

  // The sticky bar speaks for both financing types, so it discards and saves both.
  const clearAllDrafts = () => {
    drafts.discardAllDrafts();
    clearMatrixSelection();
  };

  const handleDelete = () => {
    if (!props.isDeleteAllowed) {
      return;
    }

    openConfirmDialog({
      onConfirm: () =>
        deleteFinancing({saleVehicleId: props.saleVehicleId, type: activeType})
          .unwrap()
          .then(() => {
            clearActiveDraft();
            showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
          })
          .catch(handleApiError),
    });
  };

  const handleUseAutomatic = () => {
    if (!props.isUpdateAllowed) {
      return;
    }

    activateAutomaticFinancing({saleVehicleId: props.saleVehicleId, type: activeType})
      .unwrap()
      .then(() => {
        clearActiveDraft();
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);
  };

  const handleConfigureManually = () => {
    const manualBody = createManualFinancingBody(activeType, {isGermanTenant});

    setSelectedBalloon(manualBody.activeBalloonPayment);
    drafts.setActiveDraft(manualBody);
  };

  const handleTemplateSelectionCleared = () =>
    overview.hasFinancingInEffect ? handleDelete() : clearActiveDraft();

  const templates = useFinancingTemplateSelection({
    canReadTemplates: props.canReadTemplates,
    activeType,
    vehicleFinancingBody,
    getDrafts: drafts.getDrafts,
    getPersistedBody: overview.getPersistedBody,
    applyDraft: drafts.applyDraft,
    onActiveTypeChange: setActiveType,
    onTemplateApplied: (type, appliedBody) => {
      clearMatrixSelection();
      setSelectedBalloon(appliedBody.activeBalloonPayment);
      setActiveType(type);
    },
    onTemplateRebased: clearMatrixSelection,
    onSelectionCleared: handleTemplateSelectionCleared,
  });

  const displayedVehicleFinancingBody =
    vehicleFinancingBody ?? createManualFinancingBody(activeType, {isGermanTenant});
  const isTemplatePending =
    displayedVehicleFinancingBody.mode === 'TEMPLATE' &&
    (templates.isTemplateLoading || templates.isTemplateError);
  const isFinancialInstitutionMissing =
    drafts.financialInstitutionErrorType === activeType &&
    vehicleFinancingBody?.mode === 'MANUAL' &&
    isNilOrEmpty(vehicleFinancingBody.financialInstitutionCodeId);
  // The fee the backend prices with (`FinancingOfferSource`): a template-bound configuration falls
  // back to its template's fee, a manual one has only its own override. A template fee the panel
  // cannot read is unknown rather than absent, so gross credit and total amount are not quoted.
  const templateProcessingFee: VehicleFinancingPricing['processingFee'] =
    isTemplatePending || !props.canReadTemplates || isNil(templates.selectedTemplate)
      ? 'UNKNOWN'
      : templates.selectedTemplate.processingFee;
  const processingFee =
    displayedVehicleFinancingBody.mode === 'TEMPLATE'
      ? (displayedVehicleFinancingBody.processingFeeOverride ?? templateProcessingFee)
      : displayedVehicleFinancingBody.processingFeeOverride;
  const pricing: VehicleFinancingPricing | null = isNotNil(overview.vehiclePriceWithVat)
    ? {vehiclePriceWithVat: overview.vehiclePriceWithVat, processingFee}
    : null;
  const isEditingAllowed =
    overview.isWriteAllowed && !drafts.isSaving && !templates.isApplyingTemplate;
  const balloonPayments = getBalloonBrackets(vehicleFinancingBody, activeType);
  const activeBalloon = resolveActiveBalloonPayment({
    availableBalloons: balloonPayments,
    selectedBalloon,
    activeBalloon: vehicleFinancingBody?.activeBalloonPayment ?? null,
    defaultBalloon:
      templates.selectedTemplate?.defaultBalloonPayment ??
      (vehicleFinancingBody?.mode === 'MANUAL' && activeType === 'BALLOON'
        ? DEFAULT_BALLOON_PAYMENT
        : null),
  });

  const isTypeConfigured = (type: VehicleFinancingType) =>
    isNotNil(drafts.drafts[type]) ||
    FINANCING_STATES_IN_EFFECT.includes(overview.getFinancingState(type));
  const canSwitchToAutomatic = props.isUpdateAllowed && overview.financingState === 'EXPLICIT';
  const hasFinancingActions =
    overview.hasFinancingInEffect && (canSwitchToAutomatic || props.isDeleteAllowed);
  const isAutomaticFinancingOffered =
    overview.financingState === 'EXPIRED' || overview.financingState === 'CLEARED';

  return (
    <>
      <DataStatus
        isLoading={overview.isLoading || (props.canReadTemplates && templates.areTemplatesLoading)}
        isError={overview.isError || (props.canReadTemplates && templates.areTemplatesError)}
      >
        <VStack spacing={4}>
          <FinancingTypeSegment
            value={activeType}
            isTypeConfigured={isTypeConfigured}
            onChange={(type) => {
              templates.discardPendingApplication();
              setActiveType(type);
            }}
          />

          <Show when={overview.financingState === 'EXPIRED'}>
            <Alert
              variant="warning"
              title={i18n.t('entity.vehicleFinancingTemplate.status.expired')}
              message={i18n.t('page.vehicle.financingAndLeasing.expiredFinancingExplanation')}
            />
          </Show>
          <Show when={overview.financingState === 'CLEARED'}>
            <Alert
              variant="info"
              title={i18n.t('page.vehicle.financingAndLeasing.noFinancing')}
              message={i18n.t('page.vehicle.financingAndLeasing.clearedFinancingExplanation')}
            />
          </Show>

          <FinancingBasicInformation
            vehicleFinancingBody={vehicleFinancingBody}
            canReadTemplates={props.canReadTemplates}
            template={templates.selectedTemplate}
            selectedTemplateId={templates.selectedTemplateId}
            templateOptions={templates.templateOptions}
            areTemplatesLoading={templates.areTemplatesLoading}
            isTemplateError={templates.isTemplateError}
            financialInstitutionError={
              isFinancialInstitutionMissing ? i18n.t('general.errors.mixed.required') : null
            }
            isEditingAllowed={isEditingAllowed}
            isTemplateChoiceBusy={templates.isApplyingTemplate || deleteStatus.isLoading}
            isTemplateChoiceClearable={!overview.hasFinancingInEffect || props.isDeleteAllowed}
            canOpenConfiguration={
              props.isCreateAllowed || props.isUpdateAllowed || props.isDeleteAllowed
            }
            onTemplateChange={templates.handleTemplateChange}
            onOpenConfiguration={() => openConfigurationDialog('MANAGE')}
            onChange={drafts.setActiveDraft}
          />

          <Show when={isNil(vehicleFinancingBody)}>
            <FinancingEmptyState
              canReadTemplates={props.canReadTemplates}
              isAutomaticFinancingOffered={isAutomaticFinancingOffered}
              isCreateAllowed={props.isCreateAllowed}
              isUpdateAllowed={props.isUpdateAllowed}
              isActivatingAutomatic={automaticStatus.isLoading}
              onUseAutomatic={handleUseAutomatic}
              onCreateTemplate={() => openConfigurationDialog('CREATE')}
              onConfigureManually={handleConfigureManually}
            />
          </Show>

          <Show when={vehicleFinancingBody}>
            <FinancingRateMatrixSection
              vehicleFinancingBody={displayedVehicleFinancingBody}
              type={activeType}
              template={templates.selectedTemplate}
              displayMode={displayMode}
              balloonPayments={balloonPayments}
              activeBalloon={activeBalloon}
              vehiclePrice={overview.vehiclePrice}
              currency={overview.currency}
              pricing={pricing}
              isTemplatePending={isTemplatePending}
              isReadOnly={!isEditingAllowed}
              hasActiveVariantError={
                drafts.activeSelectionErrorType === activeType &&
                isNotNil(vehicleFinancingBody) &&
                !hasValidActiveFinancingCell(vehicleFinancingBody)
              }
              selectionEpoch={matrixSelectionEpoch}
              onDisplayModeChange={setDisplayMode}
              onSelectedBalloonChange={setSelectedBalloon}
              onEditCell={setEditedCellCoordinates}
              onOpenConfiguration={
                props.canReadTemplates ? () => openConfigurationDialog('MANAGE') : undefined
              }
              onChange={drafts.setActiveDraft}
            />

            <FinancingOverrides
              vehicleFinancingBody={displayedVehicleFinancingBody}
              template={templates.selectedTemplate}
              currency={overview.currency}
              isReadOnly={!isEditingAllowed}
              onOpenConfiguration={
                props.canReadTemplates ? () => openConfigurationDialog('MANAGE') : undefined
              }
              onChange={drafts.setActiveDraft}
            />

            <Show when={hasFinancingActions}>
              <ButtonGroup align="right">
                <Show when={canSwitchToAutomatic}>
                  <Button
                    title={i18n.t('page.vehicle.financingAndLeasing.useAutomaticFinancing')}
                    variant="secondary"
                    isLoading={automaticStatus.isLoading}
                    onClick={handleUseAutomatic}
                  />
                </Show>
                <Show when={props.isDeleteAllowed}>
                  <Button
                    title={i18n.t('page.vehicle.financingAndLeasing.clearFinancing')}
                    variant="secondary"
                    leftIcon="action/delete"
                    isLoading={deleteStatus.isLoading}
                    onClick={handleDelete}
                  />
                </Show>
              </ButtonGroup>
            </Show>
          </Show>

          <Show when={drafts.isDirty}>
            <PendingChangesBar
              title={getFinancingPendingChangesTitle(drafts.drafts)}
              isSaving={drafts.isSaving}
              onDiscard={clearAllDrafts}
              onSave={drafts.saveDirtyDrafts}
            />
          </Show>
        </VStack>
      </DataStatus>

      <Show when={isNotNil(editedCellCoordinates) && isNotNil(vehicleFinancingBody)}>
        <FinancingCellEditorDialog
          type={activeType}
          cellCoordinates={editedCellCoordinates}
          vehicleFinancingBody={vehicleFinancingBody}
          balloonPayments={balloonPayments}
          activeBalloon={activeBalloon}
          vehiclePrice={overview.vehiclePrice}
          currency={overview.currency}
          pricing={pricing}
          isReadOnly={!isEditingAllowed}
          onChange={drafts.setActiveDraft}
          onSelectedBalloonChange={setSelectedBalloon}
          onClose={() => setEditedCellCoordinates(null)}
        />
      </Show>

      <Show when={props.canReadTemplates}>
        <FinancingConfigurationDialog
          // `configurationDialogKey` already changes on every open, which is what re-seeds the
          // fallback template. Keying on `activeType` too remounted the dialog mid-flow when
          // creating a template of the other type, wiping the form the user had just saved.
          key={`financing-template-manager-${configurationDialogKey}`}
          fallbackTemplate={createVehicleFinancingTemplateSeed(
            activeType,
            displayedVehicleFinancingBody
          )}
          initialTemplateId={templates.selectedTemplateId}
          isOpen={isConfigurationDialogOpen}
          startWithNewTemplate={configurationDialogMode === 'CREATE'}
          onCreated={isEditingAllowed ? templates.handleTemplateCreated : undefined}
          onUpdated={templates.handleTemplateUpdated}
          onPendingChange={drafts.handleConfigurationPendingChange}
          onSavingChange={props.onSavingChange}
          onClose={closeConfigurationDialog}
        />
      </Show>
    </>
  );
}
